import { z } from 'zod';

const RELEASE_BASE_URL = 'https://syncly-public.s3.us-east-1.amazonaws.com/scout-release';

const DownloadSchema = z.object({
  url: z
    .string()
    .min(1)
    .refine((value) => /^[^/:?#\\]+$/.test(value)),
  size: z.number().int().positive(),
});

const WebDownloadManifestSchema = z.discriminatedUnion('platform', [
  z.object({
    schemaVersion: z.literal(1),
    product: z.literal('creator-scout'),
    platform: z.literal('mac'),
    channel: z.literal('stable'),
    version: z.string().min(1),
    architecture: z.enum(['arm64', 'x64']),
    download: DownloadSchema.extend({
      url: DownloadSchema.shape.url.refine((value) => value.endsWith('.dmg')),
    }),
    releaseDate: z.iso.datetime(),
  }),
  z.object({
    schemaVersion: z.literal(1),
    product: z.literal('creator-scout'),
    platform: z.literal('win'),
    channel: z.literal('stable'),
    version: z.string().min(1),
    architecture: z.literal('x64'),
    download: DownloadSchema.extend({
      url: DownloadSchema.shape.url.refine((value) => value.endsWith('.exe')),
    }),
    releaseDate: z.iso.datetime(),
  }),
]);

export type ScoutDownloadPlatform = 'mac' | 'win';
export type ScoutDownloadArchitecture = 'arm64' | 'x64';

export interface ScoutDownload {
  platform: ScoutDownloadPlatform;
  version: string;
  architecture: ScoutDownloadArchitecture;
  url: string;
  size: number;
}

interface PlatformSignals {
  userAgentDataPlatform?: string;
  platform?: string;
  userAgent?: string;
}

export function detectScoutDownloadPlatform({
  userAgentDataPlatform = '',
  platform = '',
  userAgent = '',
}: PlatformSignals): ScoutDownloadPlatform | null {
  const signals = `${userAgentDataPlatform} ${platform} ${userAgent}`.toLowerCase();

  if (/android|iphone|ipad|ipod/.test(signals)) return null;
  if (/windows|win32|win64/.test(signals)) return 'win';
  if (/macos|macintosh|macintel|mac os/.test(signals)) return 'mac';
  return null;
}

export function getScoutDownloadPlatform(): ScoutDownloadPlatform | null {
  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };

  return detectScoutDownloadPlatform({
    userAgentDataPlatform: navigatorWithUserAgentData.userAgentData?.platform,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
  });
}

export async function fetchScoutDownloads(
  platform: ScoutDownloadPlatform,
  signal?: AbortSignal,
): Promise<ScoutDownload[]> {
  const architectures: ScoutDownloadArchitecture[] =
    platform === 'mac' ? ['arm64', 'x64'] : ['x64'];

  return Promise.all(
    architectures.map((architecture) => fetchScoutDownload(platform, architecture, signal)),
  );
}

async function fetchScoutDownload(
  platform: ScoutDownloadPlatform,
  architecture: ScoutDownloadArchitecture,
  signal?: AbortSignal,
): Promise<ScoutDownload> {
  const platformPath = platform === 'mac' ? `${platform}/${architecture}` : platform;
  const manifestUrl = `${RELEASE_BASE_URL}/${platformPath}/stable/web-download-latest.json`;
  const response = await fetch(manifestUrl, { signal });

  if (!response.ok) {
    throw new Error(`Failed to load Creator Scout download metadata: ${response.status}`);
  }

  const manifest = WebDownloadManifestSchema.parse(await response.json());
  if (manifest.platform !== platform || manifest.architecture !== architecture) {
    throw new Error('Creator Scout download target does not match the request.');
  }

  return {
    platform: manifest.platform,
    version: manifest.version,
    architecture: manifest.architecture,
    url: new URL(manifest.download.url, manifestUrl).toString(),
    size: manifest.download.size,
  };
}
